/**
 * PublicPagerChromeProvider — supplies the localized `PagerChrome` bundle to
 * the shared `@letsgethome/ui/paginated-directory` render door (the numbered
 * pager on the blog/partners/estates/saved/conversations directories).
 *
 * The ui package is framework-agnostic and cannot depend on react-intl, so the
 * public app builds the localized strings here (from the typed `pager.*`
 * catalog) and injects them once at the root via the package's context. The
 * value is a fully-typed `PagerChrome`, so a missing key is a compile error;
 * `gotoPage`/`pageOf` are closures because their args are per-render.
 *
 * Must render BELOW `IntlProvider` (it reads `useTranslations`). Mirrors the
 * staff `StaffUiChromeProvider` (ADR 0143).
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { PagerChromeProvider, type PagerChrome } from '@letsgethome/ui/pager-chrome';
import { useMemo, type ReactNode } from 'react';

export function PublicPagerChromeProvider({ children }: { children: ReactNode }) {
  const t = useTranslations('pager');

  const value = useMemo<PagerChrome>(
    () => ({
      label: t('label'),
      previous: t('previous'),
      next: t('next'),
      gotoPage: (vars) => t('gotoPage', vars),
      pageOf: (vars) => t('pageOf', vars),
    }),
    [t]
  );

  return <PagerChromeProvider value={value}>{children}</PagerChromeProvider>;
}
